import { ContractEntitySubTypeEnum, NetworkIntegrityAggregateKey, RegionSetStatusType } from "@app/projects/enumerators";
import { AccessGapTypeEnum } from "@app/projects/network-assessment/provider-hub/enumerators";
import { AccessPointSortOptionEnum, AggregateContractEntity, AggregateServicingProvider, ProviderHubContractEntitySortColumn, ProviderHubCountiesImpacted, ProviderHubHospitalSortColumn, ProviderHubOrganizationSortColumn, ProviderHubSortColumn, ProviderHubViewEnum, ProviderProfileActionEnum, VirtualAssistantContext } from "@app/projects/network-assessment/provider-hub/interfaces";
import { GeoInfo } from "@app/tools/geocoderService";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { isNullOrUndefined } from "@app/shared/utility/isNullOrUndefined";
import { sortStringsCaseInsensitive } from "@app/shared/utility/sortStringsCaseInsensitive";
import { toast } from "react-toastify";

export const NO_ACTIONS_APPLIED_LABEL = 'No Actions Applied';

// Derives the Ask-Quinn virtual-assistant provider context from the provider-hub view state. When the user is
// on a provider profile, the servicing provider key/name are surfaced so the chat can include them in its
// system message; otherwise they are cleared. isOpen is intentionally omitted — it is owned by the panel
// open/close callbacks and the merging setVirtualAssistantContext preserves it.
export const buildAskQuinnProviderContext = (
  view: ProviderHubViewEnum,
  selectedProvider: AggregateServicingProvider | undefined
): VirtualAssistantContext => {
  return view === ProviderHubViewEnum.ProviderProfile && !!selectedProvider
    ? { servicingProviderKey: selectedProvider.servicingProviderKey, servicingProviderName: selectedProvider.name }
    : { servicingProviderKey: undefined, servicingProviderName: undefined };
};

// Derives the Ask-Quinn virtual-assistant contract entity context from the provider-hub view state, mirroring
// buildAskQuinnProviderContext above. isOpen is intentionally omitted for the same reason.
export const buildAskQuinnContractEntityContext = (
  view: ProviderHubViewEnum,
  selectedContractEntity: AggregateContractEntity | undefined
): VirtualAssistantContext => {
  return view === ProviderHubViewEnum.ContractEntityProfile && !!selectedContractEntity
    ? { contractEntityId: selectedContractEntity.contractEntityId, contractEntityName: selectedContractEntity.name }
    : { contractEntityId: undefined, contractEntityName: undefined };
};

const getProviderHubActionVerb = (action: ProviderProfileActionEnum): string => {
  switch (action) {
    case ProviderProfileActionEnum.Exclude: return "excluded";
    case ProviderProfileActionEnum.Terminate: return "terminated";
    default: return "targeted";
  }
};

// Success toast (top of page) shown after a provider-hub Target/Exclude/Terminate action is submitted.
// `subject` is the entity acted on (e.g. "Hospital", "Contract Entity", "Organization"). `runLater` true =>
// submitted for deferred processing ("Run Project Analysis Later"); false => analysis kicked off immediately.
export const showProviderHubActionToast = (subject: string, action: ProviderProfileActionEnum, runLater: boolean): void => {
  toast(
    `${subject} has been successfully ${getProviderHubActionVerb(action)}${runLater ? " and submitted as 'Run Project Analysis Later.'" : "."}`,
    { containerId: "z-index-9999", type: "success" }
  );
};

export const AccessGapTypeEnumToDisplay = (gapType: AccessGapTypeEnum): string => {
  switch (gapType) {
    case AccessGapTypeEnum.NoGap:
      return "No Gap";
    case AccessGapTypeEnum.UnfillableGap:
      return "Unfillable Gap";
    case AccessGapTypeEnum.PartiallyFillableGap:
      return "Partially Fillable Gap";
    case AccessGapTypeEnum.FillableBySingleProvider:
      return "Fillable By Single Provider";
    case AccessGapTypeEnum.FillableGap:
      return "Fillable Gap";
    case AccessGapTypeEnum.Unknown:
    default:
      return "---";
  }
};

// County Status display for the Gap Closure fly-out table. Values map 1:1 to the enum labels; Unknown/missing shows "---".
export const RegionSetStatusToDisplay = (status: RegionSetStatusType | undefined): string => {
  switch (status) {
    case RegionSetStatusType.Active:
      return "Active";
    case RegionSetStatusType.Pending:
      return "Pending";
    case RegionSetStatusType.Approved:
      return "Approved";
    case RegionSetStatusType.Opportunity:
      return "Opportunity";
    default:
      return "---";
  }
};

// Default-sort rank for the Gap Closure County Status column: current service-area rows
// (Active/Pending/Approved) first, Opportunity below, unknown/missing last.
export const regionSetStatusSortRank = (status: RegionSetStatusType | undefined): number => {
  switch (status) {
    case RegionSetStatusType.Active:
    case RegionSetStatusType.Pending:
    case RegionSetStatusType.Approved:
      return 0;
    case RegionSetStatusType.Opportunity:
      return 1;
    default:
      return 2;
  }
};

export const GeoInfoToDisplay = (geoInfo: GeoInfo): string => {
  switch (geoInfo) {
    case GeoInfo.addressExact:
      return "Address-Exact";
    case GeoInfo.addressRelaxed:
      return "Address-Relaxed";
    case GeoInfo.zipDistributive:
      return "Zip-Distributive";
    case GeoInfo.zipLegacy:
      return "Zip-Legacy";
    case GeoInfo.zipPoint:
      return "Zip-Point";
    case GeoInfo.invalidZip:
      return "Zip-Invalid";
    case GeoInfo.notInLicensedState:
      return "Not Geocoded - Not In Licensed State";
    case GeoInfo.correctedZipNotInLicensedState:
      return "Not Geocoded - Corrected Zip Not In Licensed State";
    case GeoInfo.addressRelaxedZipMismatch:
      return "Address-Relaxed - Zip Mismatched";
    case GeoInfo.failOnInvalidZip:
      return "Not Geocoded - Invalid Zip Code";
    case GeoInfo.failOnNotInCountyClassFilter:
      return "Not Geocoded - Not In County Class Filter";
    case GeoInfo.failOnNotInServiceAreaFilter:
      return "Not Geocoded - Not In Service Area Filter";
    case GeoInfo.failOnNotInZipClassFilter:
      return "Not Geocoded - Not In Zip Code Class Filter";
    case GeoInfo.notYetProcessed:
      return "Not Geocoded - Not Yet Processed";
    case GeoInfo.unknown:
    default:
      return "---";
  }
};

export const SortByOptionToDisplay = (sortByOption: ProviderHubSortColumn): string => {
  switch (sortByOption) {
    case ProviderHubSortColumn.NameAscending:
      return "Name (A → Z)";
    case ProviderHubSortColumn.NameDescending:
      return "Name (Z → A)";
    case ProviderHubSortColumn.GapsClosedDescending:
      return "Gap Impact (Most Impact)";
    case ProviderHubSortColumn.GapsClosedAscending:
      return "Gap Impact (Least Impact)";
    case ProviderHubSortColumn.ActiveGapsClosedDescending:
      return "Active Gap Impact (Most Impact)";
    case ProviderHubSortColumn.ActiveGapsClosedAscending:
      return "Active Gap Impact (Least Impact)";
    case ProviderHubSortColumn.PendingGapsClosedDescending:
      return "Pending Gap Impact (Most Impact)";
    case ProviderHubSortColumn.PendingGapsClosedAscending:
      return "Pending Gap Impact (Least Impact)";
    case ProviderHubSortColumn.ApprovedGapsClosedDescending:
      return "Approved Gap Impact (Most Impact)";
    case ProviderHubSortColumn.ApprovedGapsClosedAscending:
      return "Approved Gap Impact (Least Impact)";
    case ProviderHubSortColumn.OpportunityGapsClosedDescending:
      return "Opportunity Gap Impact (Most Impact)";
    case ProviderHubSortColumn.OpportunityGapsClosedAscending:
      return "Opportunity Gap Impact (Least Impact)";
    case ProviderHubSortColumn.AccessPointCountDescending:
      return "Access Points (Most Access Points)";
    case ProviderHubSortColumn.AccessPointCountAscending:
      return "Access Points (Least Access Points)";
    case ProviderHubSortColumn.EffectivenessDescending:
      return "Effectiveness (Quality) - Highest First";
    case ProviderHubSortColumn.EffectivenessAscending:
      return "Effectiveness (Quality) - Lowest First";
    case ProviderHubSortColumn.EfficiencyDescending:
      return "Efficiency (Cost) - Most Efficient Providers First";
    case ProviderHubSortColumn.EfficiencyAscending:
      return "Efficiency (Cost) - Least Efficient Providers First";
  }
};

export const SortContractEntityByOptionToDisplay = (sortByOption: ProviderHubContractEntitySortColumn): string => {
  switch (sortByOption) {
    case ProviderHubContractEntitySortColumn.NameAscending:
      return "Name (A -> Z)";
    case ProviderHubContractEntitySortColumn.NameDescending:
      return "Name (Z -> A)";
    case ProviderHubContractEntitySortColumn.GapsClosedDescending:
      return "Gap Impact (Most Impact)";
    case ProviderHubContractEntitySortColumn.GapsClosedAscending:
      return "Gap Impact (Least Impact)";
    case ProviderHubContractEntitySortColumn.ActiveGapsClosedDescending:
      return "Active Gap Impact (Most Impact)";
    case ProviderHubContractEntitySortColumn.ActiveGapsClosedAscending:
      return "Active Gap Impact (Least Impact)";
    case ProviderHubContractEntitySortColumn.PendingGapsClosedDescending:
      return "Pending Gap Impact (Most Impact)";
    case ProviderHubContractEntitySortColumn.PendingGapsClosedAscending:
      return "Pending Gap Impact (Least Impact)";
    case ProviderHubContractEntitySortColumn.ApprovedGapsClosedDescending:
      return "Approved Gap Impact (Most Impact)";
    case ProviderHubContractEntitySortColumn.ApprovedGapsClosedAscending:
      return "Approved Gap Impact (Least Impact)";
    case ProviderHubContractEntitySortColumn.OpportunityGapsClosedDescending:
      return "Opportunity Gap Impact (Most Impact)";
    case ProviderHubContractEntitySortColumn.OpportunityGapsClosedAscending:
      return "Opportunity Gap Impact (Least Impact)";
    case ProviderHubContractEntitySortColumn.EntityTypeAscending:
      return "Entity Type Ascending";
    case ProviderHubContractEntitySortColumn.EntityTypeDescending:
      return "Entity Type Descending";
    case ProviderHubContractEntitySortColumn.FilteredCostAscending:
      return "Efficiency (Cost) - Least Efficient Providers First";
    case ProviderHubContractEntitySortColumn.FilteredCostDescending:
      return "Efficiency (Cost) - Most Efficient Providers First";
    case ProviderHubContractEntitySortColumn.FilteredInNetworkServicingProvidersAscending:
      return "In-Network Providers (Low -> High)";
    case ProviderHubContractEntitySortColumn.FilteredInNetworkServicingProvidersDescending:
      return "In-Network Providers (High -> Low)";
    case ProviderHubContractEntitySortColumn.FilteredOutOfNetworkServicingProvidersAscending:
      return "Out of Network Providers (Low -> High)";
    case ProviderHubContractEntitySortColumn.FilteredOutOfNetworkServicingProvidersDescending:
      return "Out of Network Providers (High -> Low)";
    case ProviderHubContractEntitySortColumn.FilteredQualityAscending:
      return "Effectiveness (Quality) - Lowest First";
    case ProviderHubContractEntitySortColumn.FilteredQualityDescending:
      return "Effectiveness (Quality) - Highest First";
    case ProviderHubContractEntitySortColumn.FilteredServicingProvidersAscending:
      return "Servicing Providers (Low -> High)";
    case ProviderHubContractEntitySortColumn.FilteredServicingProvidersDescending:
      return "Servicing Providers (High -> Low)";
    case ProviderHubContractEntitySortColumn.FilteredServicingSpecialtiesAscending:
      return "Specialties (Low -> High)";
    case ProviderHubContractEntitySortColumn.FilteredServicingSpecialtiesDescending:
      return "Specialties (High -> Low)";
    case ProviderHubContractEntitySortColumn.FilteredMotiveAppropriatenessDescending:
      return "Motive Appropriateness Score (High -> Low)";
    case ProviderHubContractEntitySortColumn.FilteredMotiveAppropriatenessAscending:
      return "Motive Appropriateness Score (Low -> High)";
    case ProviderHubContractEntitySortColumn.FilteredCareJourneyCostDescending:
      return "CareJourney Cost (High -> Low)";
    case ProviderHubContractEntitySortColumn.FilteredCareJourneyCostAscending:
      return "CareJourney Cost (Low -> High)";
    case ProviderHubContractEntitySortColumn.FilteredCareJourneyQualityDescending:
      return "CareJourney Quality (High -> Low)";
    case ProviderHubContractEntitySortColumn.FilteredCareJourneyQualityAscending:
      return "CareJourney Quality (Low -> High)"
  }
};

export const SortOrganizationByOptionToDisplay = (sortByOption: ProviderHubOrganizationSortColumn): string => {
  switch (sortByOption) {
    case ProviderHubOrganizationSortColumn.NameAscending:
      return "Name (A -> Z)";
    case ProviderHubOrganizationSortColumn.NameDescending:
      return "Name (Z -> A)";
    case ProviderHubOrganizationSortColumn.GapsClosedDescending:
      return "Gap Impact (Most Impact)";
    case ProviderHubOrganizationSortColumn.GapsClosedAscending:
      return "Gap Impact (Least Impact)";
    case ProviderHubOrganizationSortColumn.ActiveGapsClosedDescending:
      return "Active Gap Impact (Most Impact)";
    case ProviderHubOrganizationSortColumn.ActiveGapsClosedAscending:
      return "Active Gap Impact (Least Impact)";
    case ProviderHubOrganizationSortColumn.PendingGapsClosedDescending:
      return "Pending Gap Impact (Most Impact)";
    case ProviderHubOrganizationSortColumn.PendingGapsClosedAscending:
      return "Pending Gap Impact (Least Impact)";
    case ProviderHubOrganizationSortColumn.ApprovedGapsClosedDescending:
      return "Approved Gap Impact (Most Impact)";
    case ProviderHubOrganizationSortColumn.ApprovedGapsClosedAscending:
      return "Approved Gap Impact (Least Impact)";
    case ProviderHubOrganizationSortColumn.OpportunityGapsClosedDescending:
      return "Opportunity Gap Impact (Most Impact)";
    case ProviderHubOrganizationSortColumn.OpportunityGapsClosedAscending:
      return "Opportunity Gap Impact (Least Impact)";
    case ProviderHubOrganizationSortColumn.FilteredCostAscending:
      return "Efficiency (Cost) - Least Efficient Providers First";
    case ProviderHubOrganizationSortColumn.FilteredCostDescending:
      return "Efficiency (Cost) - Most Efficient Providers First";
    case ProviderHubOrganizationSortColumn.FilteredInNetworkServicingProvidersAscending:
      return "In-Network Providers (Low -> High)";
    case ProviderHubOrganizationSortColumn.FilteredInNetworkServicingProvidersDescending:
      return "In-Network Providers (High -> Low)";
    case ProviderHubOrganizationSortColumn.FilteredOutOfNetworkServicingProvidersAscending:
      return "Out of Network Providers (Low -> High)";
    case ProviderHubOrganizationSortColumn.FilteredOutOfNetworkServicingProvidersDescending:
      return "Out of Network Providers (High -> Low)";
    case ProviderHubOrganizationSortColumn.FilteredQualityAscending:
      return "Effectiveness (Quality) - Lowest First";
    case ProviderHubOrganizationSortColumn.FilteredQualityDescending:
      return "Effectiveness (Quality) - Highest First";
    case ProviderHubOrganizationSortColumn.FilteredServicingProvidersAscending:
      return "Servicing Providers (Low -> High)";
    case ProviderHubOrganizationSortColumn.FilteredServicingProvidersDescending:
      return "Servicing Providers (High -> Low)";
    case ProviderHubOrganizationSortColumn.FilteredServicingSpecialtiesAscending:
      return "Specialties (Low -> High)";
    case ProviderHubOrganizationSortColumn.FilteredServicingSpecialtiesDescending:
      return "Specialties (High -> Low)";
    case ProviderHubOrganizationSortColumn.FilteredMotiveAppropriatenessDescending:
      return "Motive Appropriateness Score (High -> Low)";
    case ProviderHubOrganizationSortColumn.FilteredMotiveAppropriatenessAscending:
      return "Motive Appropriateness Score (Low -> High)";
    case ProviderHubOrganizationSortColumn.FilteredCareJourneyCostDescending:
      return "CareJourney Cost (High -> Low)";
    case ProviderHubOrganizationSortColumn.FilteredCareJourneyCostAscending:
      return "CareJourney Cost (Low -> High)";
    case ProviderHubOrganizationSortColumn.FilteredCareJourneyQualityDescending:
      return "CareJourney Quality (High -> Low)";
    case ProviderHubOrganizationSortColumn.FilteredCareJourneyQualityAscending:
      return "CareJourney Quality (Low -> High)"
  }
}

export const SortHospitalByOptionToDisplay = (sortByOption: ProviderHubHospitalSortColumn): string => {
  switch (sortByOption) {
    case ProviderHubHospitalSortColumn.NameAscending:
      return "Name (A - Z)";
    case ProviderHubHospitalSortColumn.NameDescending:
      return "Name (Z - A)";
    case ProviderHubHospitalSortColumn.GapImpactDescending:
      return "Gap Impact (Most Impact)";
    case ProviderHubHospitalSortColumn.GapImpactAscending:
      return "Gap Impact (Least Impact)";
    case ProviderHubHospitalSortColumn.ActiveGapImpactDescending:
      return "Active Gap Impact (Most Impact)";
    case ProviderHubHospitalSortColumn.ActiveGapImpactAscending:
      return "Active Gap Impact (Least Impact)";
    case ProviderHubHospitalSortColumn.PendingGapImpactDescending:
      return "Pending Gap Impact (Most Impact)";
    case ProviderHubHospitalSortColumn.PendingGapImpactAscending:
      return "Pending Gap Impact (Least Impact)";
    case ProviderHubHospitalSortColumn.ApprovedGapImpactDescending:
      return "Approved Gap Impact (Most Impact)";
    case ProviderHubHospitalSortColumn.ApprovedGapImpactAscending:
      return "Approved Gap Impact (Least Impact)";
    case ProviderHubHospitalSortColumn.OpportunityGapImpactDescending:
      return "Opportunity Gap Impact (Most Impact)";
    case ProviderHubHospitalSortColumn.OpportunityGapImpactAscending:
      return "Opportunity Gap Impact (Least Impact)";
    case ProviderHubHospitalSortColumn.FilteredServicingProvidersDescending:
      return "Servicing Providers (High -> Low)";
    case ProviderHubHospitalSortColumn.FilteredServicingProvidersAscending:
      return "Servicing Providers (Low -> High)";
    case ProviderHubHospitalSortColumn.FilteredServicingSpecialtiesDescending:
      return "Specialties (High -> Low)";
    case ProviderHubHospitalSortColumn.FilteredServicingSpecialtiesAscending:
      return "Specialties (Low -> High)";
    case ProviderHubHospitalSortColumn.FilteredOutOfNetworkServicingProvidersDescending:
      return "Out of Network Providers (High -> Low)";
    case ProviderHubHospitalSortColumn.FilteredOutOfNetworkServicingProvidersAscending:
      return "Out of Network Providers (Low -> High)";
    case ProviderHubHospitalSortColumn.FilteredInNetworkServicingProvidersDescending:
      return "In-Network Providers (High -> Low)";
    case ProviderHubHospitalSortColumn.FilteredInNetworkServicingProvidersAscending:
      return "In-Network Providers (Low -> High)";
    case ProviderHubHospitalSortColumn.FilteredQualityDescending:
      return "Effectiveness (Quality) - Highest First";
    case ProviderHubHospitalSortColumn.FilteredQualityAscending:
      return "Effectiveness (Quality) - Lowest First";
    case ProviderHubHospitalSortColumn.FilteredCostDescending:
      return "Efficiency (Cost) - Most Efficient Providers First";
    case ProviderHubHospitalSortColumn.FilteredCostAscending:
      return "Efficiency (Cost) - Least Efficient Providers First";
    case ProviderHubHospitalSortColumn.FilteredMotiveAppropriatenessDescending:
      return "Motive Appropriateness Score (High -> Low)";
    case ProviderHubHospitalSortColumn.FilteredMotiveAppropriatenessAscending:
      return "Motive Appropriateness Score (Low -> High)";
    case ProviderHubHospitalSortColumn.FilteredCareJourneyCostDescending:
      return "CareJourney Cost (High -> Low)";
    case ProviderHubHospitalSortColumn.FilteredCareJourneyCostAscending:
      return "CareJourney Cost (Low -> High)";
    case ProviderHubHospitalSortColumn.FilteredCareJourneyQualityDescending:
      return "CareJourney Quality (High -> Low)";
    case ProviderHubHospitalSortColumn.FilteredCareJourneyQualityAscending:
      return "CareJourney Quality (Low -> High)";
  }
}

export const getCriticalityClass = (criticalityCount: number): string => {
  if (criticalityCount >= 8) return "criticality-high";
  if (criticalityCount >= 5) return "criticality-medium";
  return "criticality-low";
};

export const getCriticalityLabel = (score?: number): string => {
  return score === undefined || score === null ? "---" : `${score}/10`;
}

export const providerNetworkTypeDisplayName = (type: ProviderNetworkTypeEnum): string => {
  switch (type) {
    case ProviderNetworkTypeEnum.Network: return "In-Network";
    case ProviderNetworkTypeEnum.Prospect: return "Prospect";
    case ProviderNetworkTypeEnum.Market: return "Market";
    case ProviderNetworkTypeEnum.NetNew: return "Net New";
    default: return "";
  }
};

export const scoreFilterValueToDisplay = (val: number | null): string => {
  return isNullOrUndefined(val) ? "Unknown" : String(val);
};

export const getContractEntitySubtype = (entityType: ContractEntitySubTypeEnum): string => {
  switch (entityType) {
    case ContractEntitySubTypeEnum.LargeIndependent:
      return "Large Independent";
    case ContractEntitySubTypeEnum.IDN:
      return "Integrated Delivery Network (IDN)";
    case ContractEntitySubTypeEnum.MediumIndependent:
      return "Medium Independent";
    case ContractEntitySubTypeEnum.NonIDNHealthSystem:
      return "Non-IDN Health System";
    case ContractEntitySubTypeEnum.SmallIndependent:
      return "Small Independent";
    default:
      return "Unknown";
  }
}

export const formatCountiesImpacted = (counties: ProviderHubCountiesImpacted[] | undefined): string[] => {
  const formatted = counties?.map(({ name, state }) => `${name}, ${state}`);
  // Deduplicate: aggregating counties across a group's providers (or across region sets) can repeat the same county.
  return sortStringsCaseInsensitive(formatted && Array.from(new Set(formatted)));
};

// Displays a score (effectiveness / efficiency / motive / care journey) rounded to at most one decimal place, so
// aggregated averages don't render as long decimals (e.g. 2.111111). A trailing ".0" is dropped so whole-number
// scores render as "4" — matching the raw list/search views — rather than "4.0". Returns "---" when there is no
// numeric value.
export const formatScore = (value: number | string | undefined | null): string => {
  if (value === null || value === undefined || value === "---") return "---";
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? (Math.round(num * 10) / 10).toString() : "---";
};

export const sortPredictedSpecialtyNames = (names: string[] | undefined): string[] => {
  return sortStringsCaseInsensitive(names);
};

// Renders a specialty as `description (code)` (e.g. "Allergy and Immunology (007)"), preserving the code's leading zeros.
// Falls back to the description alone when the code is null or blank so there are no empty parens.
export const formatSpecialtyWithCode = (specialty: { code: string | null | undefined; name: string }): string =>
  specialty.code ? `${specialty.name} (${specialty.code})` : specialty.name;

export const getNetworkIntegrityGhostProviderLobLabel = (lineOfBusiness?: string, lineOfBusinessType?: string): string | null => {
  switch (lineOfBusiness) {
    case "Medicare":
    case "Medicaid":
      return `Ghost Providers (${lineOfBusiness} Claims)`;
    case "Marketplace":
    case "Employer Sponsored":
      return "Ghost Providers (Commercial Claims)";
    case "Military": {
      const displayType = lineOfBusinessType === "VACCN" ? "VA" : lineOfBusinessType;
      return displayType ? `Ghost Providers (Military - ${displayType} Claims)` : null;
    }
    default:
      return null;
  }
};

export const accessPointSortOptionLabel = (option: AccessPointSortOptionEnum): string => {
  switch (option) {
    case AccessPointSortOptionEnum.Default: return "Default (Network first)";
    case AccessPointSortOptionEnum.LocationConfidenceHighToLow: return "Location Confidence (highest to lowest)";
    case AccessPointSortOptionEnum.OutOfNetworkFirst: return "Out of Network first";
    case AccessPointSortOptionEnum.TerminatedFirst: return "Terminated first";
  }
};

export const niHideLabels: Record<string, string> = {
  [NetworkIntegrityAggregateKey.ClosedLocation]: "Hide Closed Locations",
  [NetworkIntegrityAggregateKey.DeactivatedNpi]: "Hide Deactivated NPIs",
  [NetworkIntegrityAggregateKey.ExpiredLicensePractitioners]: "Hide Expired License Practitioners",
  [NetworkIntegrityAggregateKey.GhostProviders]: "Hide Ghost Providers (Total Claims)",
  [NetworkIntegrityAggregateKey.InvalidNpi]: "Hide Invalid NPIs",
  [NetworkIntegrityAggregateKey.LeftOffice]: "Hide Left Office",
  [NetworkIntegrityAggregateKey.LowLocationConfidence]: "Hide Low Location Confidence",
  [NetworkIntegrityAggregateKey.MedicareOptOut]: "Hide Medicare Opt Outs",
  [NetworkIntegrityAggregateKey.MedicarePrecluded]: "Hide Medicare Preclusions",
  [NetworkIntegrityAggregateKey.MissingNpi]: "Hide Missing NPIs",
  [NetworkIntegrityAggregateKey.NoLongerOnRoster]: "Hide No Longer On Roster",
  [NetworkIntegrityAggregateKey.NotRecognized]: "Hide Not Recognized",
  [NetworkIntegrityAggregateKey.OigExclusion]: "Hide OIG Exclusions",
  [NetworkIntegrityAggregateKey.RetiredPractitioner]: "Hide Retired Practitioners",
  [NetworkIntegrityAggregateKey.TermedOnRoster]: "Hide Termed On Roster",
  [NetworkIntegrityAggregateKey.VeryLowLocationConfidence]: "Hide Very Low Location Confidence",
};

export interface GapImpactBreakdownRow {
  gapsClosed?: number;
  activeGapsClosed?: number;
  pendingGapsClosed?: number;
  approvedGapsClosed?: number;
  opportunityGapsClosed?: number;
  useGapsByRegionStatus?: boolean;
}

// "> 0" not "!= null": some queries COALESCE the total to 0, so in-network rows arrive as 0 rather than null.
const closesAnyGaps = (row: GapImpactBreakdownRow): boolean =>
  (row.gapsClosed ?? 0) > 0
  || (row.activeGapsClosed ?? 0) > 0
  || (row.pendingGapsClosed ?? 0) > 0
  || (row.approvedGapsClosed ?? 0) > 0
  || (row.opportunityGapsClosed ?? 0) > 0;

// Only rows that close gaps get a vote; among those the indicator must be unanimous - never a partial mix.
// Pass a complete population, never one page - backend-paginated tables read the result-level flag instead.
export const shouldShowGapImpactBreakdown = (rows: readonly GapImpactBreakdownRow[]): boolean =>
  rows.length > 0
  && rows.every((row) => !closesAnyGaps(row) || row.useGapsByRegionStatus === true);

// A row with no gap impact reaches the UI as either null or 0, depending on why: the per-status columns
// are gated to Market/Prospect/NetNew in SQL so everything else is null, while an eligible row that
// closes nothing counts an empty array and lands on 0. Some queries also COALESCE the combined total to 0.
// All of those read as "no impact" to a user, so they must render identically.
export const formatGapImpactCount = (value?: number): number | string =>
  !value ? "---" : value;
